import React, { useMemo } from 'react';
import { useData } from 'vike-react/useData';
import { FlexiblePageInput } from '@/pages/types';
import { ReadonlyComponentRenderer } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/ReadonlyComponentRenderer';
import { ThemeProvider } from '@mutiny-pkg/bin-ui/src/rigidMicrosites/theme/ThemeProvider';
import { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { FlexiblePagePassword } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/FlexiblePagePassword';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { CustomComponentProvider } from '@mutiny-pkg/bin-ui/src/customComponents/CustomComponentProvider';
import { AnalyticsPageView } from '../AnalyticsPageView';
import './componentClasses.css';

type FlexiblePageRendererProps = {
  pageId: string;
};

export function FlexiblePageRenderer({ pageId }: FlexiblePageRendererProps) {
  const {
    componentTree,
    overrideVariables,
    password,
    passwordEnabled,
    renderStage,
    staticSiteDomain,
    bodySnippets,
    tailwindCss,
    customComponents,
    clientData,
  } = useData<FlexiblePageInput>();
  const theme = componentTree.data as unknown as ThemeProperties;

  const filesystem = useMemo(
    () => ({
      artifacts: customComponents.artifacts,
      slugToCurrentSourceHash: customComponents.slugToCurrentSourceHash,
    }),
    [customComponents],
  );
  // Compiled bundles live on the page-asset CDN at
  // `<staticSiteDomain>/assets/<sourceHash>.js`. Same URL for preview + publish — the bucket has
  // CORS configured so cross-origin customer-domain pages can fetch directly.
  const resolveCompiledUrl = useMemo(
    () => (sourceHash: string) => `https://${staticSiteDomain}/assets/${sourceHash}.js`,
    [staticSiteDomain],
  );
  // Runtime shim was uploaded into this same page-asset bucket at compile time (see
  // `customComponentFilesystemService.uploadArtifact`). Resolve the artifact's
  // `/__mutiny/runtime/react/<version>.js` import to that same-origin asset so the published
  // page has no runtime dependency on champaign — the React import loads from the customer's
  // CDN directly. Path → filename mapping mirrors `runtimeShimAssetFilename`.
  const resolveRuntimeUrl = useMemo(
    () => (specifier: string) => {
      const versionedMatch = specifier.match(/^\/__mutiny\/runtime\/react\/([\w.-]+)\.js$/);
      if (!versionedMatch) {
        throw new Error(`Unsupported runtime specifier on published page: ${specifier}`);
      }
      return `https://${staticSiteDomain}/assets/mutiny-runtime-react-${versionedMatch[1]}.js`;
    },
    [staticSiteDomain],
  );

  return (
    <ThemeProvider theme={theme}>
      <AnalyticsPageView
        endpoint={clientData.analyticsEndpoint}
        companyId={clientData.analyticsCompanyId}
        assetId={clientData.analyticsAssetId}
        preview={clientData.preview}
      />
      {tailwindCss && <style data-tailwind-ssr dangerouslySetInnerHTML={{ __html: tailwindCss }} />}
      {bodySnippets && <div style={{ display: 'none' }} dangerouslySetInnerHTML={{ __html: bodySnippets }} />}
      <CustomComponentProvider
        filesystem={filesystem}
        theme={theme as unknown as Parameters<typeof CustomComponentProvider>[0]['theme']}
        overrideVariables={overrideVariables}
        isEditor={false}
        resolveCompiledUrl={resolveCompiledUrl}
        resolveRuntimeUrl={resolveRuntimeUrl}
      >
        <ReadonlyComponentRenderer
          componentTree={componentTree}
          overrideVariables={overrideVariables}
          staticAssetsBaseURL={staticSiteDomain}
          renderStage={renderStage}
        >
          {passwordEnabled && password && (
            <FlexiblePagePassword password={password} pageId={pageId} inPreview={renderStage === RenderStage.Preview} />
          )}
        </ReadonlyComponentRenderer>
      </CustomComponentProvider>
    </ThemeProvider>
  );
}
